<template>
  <li class="scratchcard-quantity-stack-item">
    <ScratchcardDisplay
      v-if="!isLoading"
      v-bind="{
        primaryColor,
        secondaryColor,
        src,
        price: game?.priceList.chargePerCard,
      }"
      class="display"
      font-size="lg"
      :is-caption-visible="!src"
      :is-price-visible="!src && !!game?.priceList.chargePerCard"
      :jackpot="jackpot?.prize"
      :name="title"
      :style="{
        height: `${size}px`,
        width: `${size}px`,
      }"
    >
      <div class="display-overlay" />
    </ScratchcardDisplay>
  </li>
</template>

<script lang="ts" setup>
const DEFAULT_SCRATCHCARD_TEASER_OVERLAY_COLOR = '#252525'
const OPACITY_STEPS = [
  0, 0.3, 0.3556, 0.4111, 0.4667, 0.5222, 0.5778, 0.6333, 0.6889, 0.7444, 0.8,
]

type ScratchcardQuantityStackItemProps = {
  index: number
  size: number
}
const props = defineProps<ScratchcardQuantityStackItemProps>()
const { index } = toRefs(props)

const { game, isLoading, jackpot, name, visualParameters } =
  useScratchcardGame()

const { t } = useI18n()
const { toUrl } = useClientUrl()

const overlayColor = computed(() =>
  src.value ? DEFAULT_SCRATCHCARD_TEASER_OVERLAY_COLOR : primaryColor.value,
)
const overlayOpacity = computed(() => {
  const max = OPACITY_STEPS.length - 1

  if (index.value <= max) return OPACITY_STEPS[index.value]
  return OPACITY_STEPS[max - 1]
})
const primaryColor = computed(() => visualParameters.value?.primaryColor)
const reversedOrder = computed(() => index.value * -1)
const secondaryColor = computed(() => visualParameters.value?.secondaryColor)
// TODO SF-4090 remove after rollout
const src = computed(() => {
  if (!visualParameters.value?.teaserImage) return null
  return toUrl(visualParameters.value.teaserImage)
})
const title = computed(
  () => name.value ?? t('scratchcard.quantitystack.name.fallback'),
)
</script>

<style lang="scss" scoped>
.scratchcard-quantity-stack-item {
  order: v-bind(reversedOrder);
  transform: translateX(var(--end-position));
  backface-visibility: hidden;
  transform-style: preserve-3d;

  .display {
    box-shadow:
      0.1875rem 0.0625rem 0.125rem rgba(color('secondary900'), 0.1),
      0 0.375rem 0.625rem rgba(color('secondary900'), 0.1),
      inset -0.0625rem -0.0625rem 0 rgba(color('secondary100'), 0.12),
      inset -0.0625rem -0.125rem 0.125rem rgba(color('secondary100'), 0.12);

    &-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: $border-radius-md;
      box-shadow: inset 0 0 0 0.0625rem rgb(color('secondary100'), 0.4);

      &::before {
        content: '';
        position: absolute;
        inset: 0.0625rem;
        border-radius: $border-radius-md;
        background-color: v-bind(overlayColor);
        opacity: v-bind(overlayOpacity);
      }
    }
  }
}
</style>
